import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { apiService } from "../services/api";
import {
  Package,
  Plus,
  Shield,
  CheckCircle,
  BarChart3,
  Users,
  Target,
  Clock,
  Settings,
  Download,
  Upload,
} from "lucide-react";
import LoadingSpinner from "../components/LoadingSpinner";
import AssessmentExportModal from "../components/AssessmentExportModal";
import { hasGlobalAccess, hasAdminDashboard, canManageUsers } from "../types";
import toast from "react-hot-toast";

export default function DashboardPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [selectedBusinessArea, setSelectedBusinessArea] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [showExportModal, setShowExportModal] = useState(false);
  const [initialPendingRequest, setInitialPendingRequest] = useState<{
    businessArea: string;
    division: string;
    membershipStatus: "requested";
  } | null>(null);

  const { data: productsData, isLoading: productsLoading } = useQuery(
    "my-products",
    () => apiService.getMyProducts(),
    {
      enabled: !!user,
    },
  );

  const needsAccessRequest = !!user && !user.businessArea && !user.division;

  const {
    data: myMembershipRequestsData,
    isLoading: membershipRequestsLoading,
  } = useQuery(
    "my-membership-requests",
    () => apiService.getMyMembershipRequests(),
    {
      enabled: needsAccessRequest,
    },
  );

  const myMembershipRequests =
    (myMembershipRequestsData?.data as any)?.data || [];

  const pendingMembershipRequest = useMemo(
    () =>
      initialPendingRequest ||
      myMembershipRequests.find((request: any) => {
        const status = request.membershipStatus || request.status;
        return status === "requested" || status === "pending";
      }),
    [myMembershipRequests, initialPendingRequest],
  );

  const { data: businessAreasData, isLoading: businessAreasLoading } = useQuery(
    "business-areas",
    () => apiService.getBusinessAreas(),
    {
      enabled: needsAccessRequest && !pendingMembershipRequest,
    },
  );

  const { data: divisionsData, isLoading: divisionsLoading } = useQuery(
    ["divisions", selectedBusinessArea],
    () => apiService.getDivisions(selectedBusinessArea),
    {
      enabled:
        needsAccessRequest &&
        !pendingMembershipRequest &&
        !!selectedBusinessArea,
    },
  );

  const createMembershipRequestMutation = useMutation(
    (data: { businessArea: string; division: string }) =>
      apiService.createMembershipRequest(data),
    {
      onMutate: (data: { businessArea: string; division: string }) => {
        setInitialPendingRequest({
          businessArea: data.businessArea,
          division: data.division,
          membershipStatus: "requested",
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries("my-membership-requests");
        toast.success("Access request submitted");
      },
      onError: (error: any) => {
        setInitialPendingRequest(null);
        toast.error(
          error.response?.data?.error || "Failed to submit access request",
        );
      },
    },
  );

  // Fetch statistics based on user role
  const { data: sdlPeriodsData } = useQuery(
    "sdl-periods-export",
    () => apiService.getSDLPeriods(),
    {
      enabled: hasAdminDashboard(user),
    },
  );

  // Fetch business areas for export filters
  const { data: exportBusinessAreas } = useQuery(
    "export-business-areas",
    () => apiService.getBusinessAreas(),
    {
      enabled: hasAdminDashboard(user),
    },
  );

  const { data: divisionStatsData, isLoading: divisionStatsLoading } = useQuery(
    "division-statistics",
    () => apiService.getDivisionStatistics(),
    {
      enabled: user?.roleId === "division_admin",
    },
  );

  const { data: baStatsData, isLoading: baStatsLoading } = useQuery(
    "ba-statistics",
    () => apiService.getBusinessAreaStatistics(),
    {
      enabled: user?.roleId === "ba_admin",
    },
  );

  const { data: globalStatsData, isLoading: globalStatsLoading } = useQuery(
    "global-statistics",
    () => apiService.getGlobalStatistics(),
    {
      enabled:
        user?.roleId === "global_admin" || user?.roleId === "global_reader",
    },
  );

  if (
    productsLoading ||
    divisionStatsLoading ||
    baStatsLoading ||
    globalStatsLoading ||
    (needsAccessRequest && membershipRequestsLoading)
  ) {
    return <LoadingSpinner />;
  }

  const myProducts = productsData?.data?.data || [];

  // Get statistics based on user role
  const getStatsData = () => {
    if (user?.roleId === "division_admin") {
      return divisionStatsData?.data?.data;
    } else if (user?.roleId === "ba_admin") {
      return baStatsData?.data?.data;
    } else if (
      user?.roleId === "global_admin" ||
      user?.roleId === "global_reader"
    ) {
      return globalStatsData?.data?.data;
    }
    return null;
  };

  const statsData = getStatsData();

  const stats = [
    {
      name: "My Products",
      value: myProducts.length,
      icon: Package,
      href: "/products?my=true",
    },
    ...(hasAdminDashboard(user) && statsData
      ? [
          {
            name: hasGlobalAccess(user)
              ? "Total Products"
              : user?.roleId === "ba_admin"
                ? "Business Area Products"
                : "Division/Business Unit Products",
            value: statsData.totalProducts,
            icon: Shield,
            href: "/products",
          },
        ]
      : []),
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Dashboard
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Welcome back, {user?.name}. Here's an overview of your products and
          compliance status.
        </p>
      </div>

      {needsAccessRequest && (
        <div className="card mb-8">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Request Business Area Access
          </h2>
          {pendingMembershipRequest ? (
            <div className="rounded-md border border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm text-yellow-800 dark:text-yellow-200">
              Your access request for {pendingMembershipRequest.businessArea} /{" "}
              {pendingMembershipRequest.division} is pending review.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Select the business area and division/business unit you want
                access to. An admin will review your request.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Business Area
                  </label>
                  <select
                    className="input w-full"
                    value={selectedBusinessArea}
                    onChange={(event) => {
                      setSelectedBusinessArea(event.target.value);
                      setSelectedDivision("");
                    }}
                    disabled={businessAreasLoading}
                  >
                    <option value="">Select a business area</option>
                    {(businessAreasData?.data as any)?.data?.map(
                      (area: string) => (
                        <option key={area} value={area}>
                          {area}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Division/Business Unit
                  </label>
                  <select
                    className="input w-full"
                    value={selectedDivision}
                    onChange={(event) =>
                      setSelectedDivision(event.target.value)
                    }
                    disabled={!selectedBusinessArea || divisionsLoading}
                  >
                    <option value="">Select a division/business unit</option>
                    {(divisionsData?.data as any)?.data?.map(
                      (division: string) => (
                        <option key={division} value={division}>
                          {division}
                        </option>
                      ),
                    )}
                  </select>
                </div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() =>
                  createMembershipRequestMutation.mutate({
                    businessArea: selectedBusinessArea,
                    division: selectedDivision,
                  })
                }
                disabled={
                  !selectedBusinessArea ||
                  !selectedDivision ||
                  createMembershipRequestMutation.isLoading
                }
              >
                {createMembershipRequestMutation.isLoading
                  ? "Submitting..."
                  : "Submit access request"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="card hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <stat.icon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {stat.name}
                </p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  {stat.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Division Statistics for Division Admins */}
      {user?.roleId === "division_admin" && divisionStatsData?.data?.data && (
        <div className="card mb-8">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Division/Business Unit Statistics
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                    Total Products
                  </p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                    {divisionStatsData.data.data.totalProducts}
                  </p>
                </div>
                <Package className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-900 dark:text-green-200">
                    Approved (into Prod Atlas)
                  </p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                    {divisionStatsData.data.data.approvedProducts}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                {divisionStatsData.data.data.approvalRate.toFixed(1)}% approval
                rate
              </p>
            </div>
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                    Pending approval (into Prod Atlas)
                  </p>
                  <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">
                    {divisionStatsData.data.data.pendingProducts}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
              </div>
            </div>
          </div>

          {/* Assessment Completion Rates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 mb-6">
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-900 dark:text-orange-200">
                    CRA Completion
                  </p>
                  <p className="text-2xl font-bold text-orange-900 dark:text-orange-200">
                    {divisionStatsData.data.data.craCompletionRate.toFixed(1)}%
                  </p>
                </div>
                <Shield className="h-8 w-8 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                {divisionStatsData.data.data.productsWithCRA} of{" "}
                {divisionStatsData.data.data.totalProducts} products
              </p>
            </div>
          </div>

          {/* Products by Category */}
          {Object.keys(divisionStatsData.data.data.productsByCategory).length >
            0 && (
            <div>
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                Products by Category
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(
                  divisionStatsData.data.data.productsByCategory,
                ).map(([category, count]) => (
                  <div
                    key={category}
                    className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {category}
                      </span>
                      <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {count as number}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assessment Overview */}
      {hasAdminDashboard(user) &&
        statsData &&
        statsData.totalProducts > 0 &&
        (user?.roleId === "ba_admin" ||
          user?.roleId === "global_admin" ||
          user?.roleId === "global_reader" ||
          user?.roleId === "division_admin") && (
          <div className="card mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {hasGlobalAccess(user)
                  ? "Organization Assessment Overview"
                  : "Business Area Assessment Overview"}
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                      CRA Completion
                    </p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                      {statsData.craCompletionRate.toFixed(1)}%
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {statsData.productsWithCRA} of {statsData.totalProducts}{" "}
                  products assessed
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-900 dark:text-green-200">
                      SDL Completion
                    </p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                      {statsData.sdlCompletionRate.toFixed(2)}%
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {statsData.productsWithSDL} of {statsData.totalProducts}{" "}
                  products assessed
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Business Area Admin Statistics */}
      {user?.roleId === "ba_admin" && baStatsData?.data?.data && (
        <div className="card mb-8">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            Business Area Breakdown
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-1 mb-6">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-900 dark:text-green-200">
                    Approved Products
                  </p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                    {baStatsData.data.data.approvedProducts}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                {baStatsData.data.data.approvalRate.toFixed(1)}% approval rate
              </p>
            </div>
          </div>

          {/* Products by Division */}
          {Object.keys(baStatsData.data.data.productsByDivision).length > 0 && (
            <div>
              <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                Products by Division/Business Unit
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(baStatsData.data.data.productsByDivision).map(
                  ([division, count]) => (
                    <div
                      key={division}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {division}
                        </span>
                        <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {count as number}
                        </span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Global Admin/Reader Statistics */}
      {(user?.roleId === "global_admin" || user?.roleId === "global_reader") &&
        globalStatsData?.data?.data && (
          <div className="card mb-8">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Global Organization Overview
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                      Total Products
                    </p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
                      {globalStatsData.data.data.totalProducts}
                    </p>
                  </div>
                  <Package className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-900 dark:text-green-200">
                      Approved (into Prod Atlas)
                    </p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-200">
                      {globalStatsData.data.data.approvedProducts}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  {globalStatsData.data.data.approvalRate.toFixed(1)}% approval
                  rate
                </p>
              </div>
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
                      Pending approval(into Prod Atlas)
                    </p>
                    <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">
                      {globalStatsData.data.data.pendingProducts}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
            </div>

            {/* Products by Business Area */}
            {Object.keys(globalStatsData.data.data.productsByBusinessArea)
              .length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                  Products by Business Area
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(
                    globalStatsData.data.data.productsByBusinessArea,
                  ).map(([ba, count]) => (
                    <div
                      key={ba}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {ba}
                        </span>
                        <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {count as number}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      {/* Quick Actions */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/products"
            className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Package className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                View Products
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Browse all products
              </p>
            </div>
          </Link>
          <Link
            to="/products/create"
            className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                Add Product
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Create a new product
              </p>
            </div>
          </Link>
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
          >
            <Download className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                Export Assessments
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Download assessment data as Excel
              </p>
            </div>
          </button>
          {(user?.roleId === "global_admin" || user?.roleId === "ba_admin") && (
            <Link
              to="/assessments/import"
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Upload className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">Import Assessments</p>
                <p className="text-sm text-gray-500">
                  Upload assessment data from Excel
                </p>
              </div>
            </Link>
          )}
          {canManageUsers(user) && (
            <Link
              to="/users"
              className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Users className="h-6 w-6 text-primary-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  Manage Users
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Add and edit users
                </p>
              </div>
            </Link>
          )}
          {user?.roleId === "global_admin" && (
            <Link
              to="/admin"
              className="flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Settings className="h-6 w-6 text-primary-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  Admin Panel
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage users and settings
                </p>
              </div>
            </Link>
          )}
          {user?.roleId === "division_admin" &&
            divisionStatsData?.data?.data?.pendingProducts > 0 && (
              <Link
                to="/products?status=NotApproved"
                className="flex items-center p-4 border border-orange-200 dark:border-orange-700 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
              >
                <Target className="h-6 w-6 text-orange-600 dark:text-orange-400 mr-3" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    Pending Approvals
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {divisionStatsData?.data?.data?.pendingProducts || 0}{" "}
                    products awaiting approval
                  </p>
                </div>
              </Link>
            )}
        </div>
      </div>

      {/* Assessment Export Modal */}
      <AssessmentExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        periods={(sdlPeriodsData?.data?.data || []).map((p: any) => ({
          year: p.year,
          quarter: p.quarter,
          period: p.period || `${p.year}-Q${p.quarter}`,
        }))}
        businessAreas={(exportBusinessAreas?.data?.data || []).map((ba: any) =>
          typeof ba === "string" ? ba : ba.name || ba,
        )}
        divisions={[]}
      />
    </div>
  );
}
